import React, { useEffect, useMemo, useState } from "react";
import "./Header.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LogoutOutlined } from "@ant-design/icons";
import ProfileImageModal from "../../molecules/ProfileImageModal/ProfileImageModal";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import { uploadFile } from "../../../Redux/Apis/employeeApi";
import { updateUserApi } from "../../../Redux/Apis/authApi";
import { clearToken, setAuthLoading } from "../../../Redux/Slices/authSlice";
import { setProjectSliceToInitial } from "../../../Redux/Slices/projectSlice";
import { getAttendanceWebhookStatusAPI } from "../../../Redux/Apis/attendanceApi";
import { checkPermission, permissionType, hasAdminAccess } from "../../../app/utils/common/Functions";
import HeaderTimeTrackerControls from "./HeaderTimeTrackerControls";

const Header = ({ toggleTheme, theme, sidebarCollapsed, mobileMenuOpen, isMobile, handleToggle }) => {
  const { loading, user } = useSelector((state) => state.auth.auth);
  const { webhookStatus } = useSelector((state) => state.attendance);
  const [isProfileImageModalOpen, setIsProfileImageModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAdminRole = hasAdminAccess(user);

  useEffect(() => {
    if (!user) return;
    if (!isAdminRole) return;
    if (!checkPermission(permissionType.ATTENDANCE, "read users")) return;

    dispatch(getAttendanceWebhookStatusAPI());
    const poll = setInterval(() => {
      dispatch(getAttendanceWebhookStatusAPI());
    }, 60000);
    return () => clearInterval(poll);
  }, [dispatch, user, isAdminRole]);

  const webhookHealth = useMemo(() => {
    const lastWebhookAt = webhookStatus?.lastWebhookAt;
    if (!lastWebhookAt) {
      return {
        label: "Webhook Offline",
        isHealthy: false,
        title: "No webhook logs received yet.",
      };
    }
    const ageMs = Date.now() - new Date(lastWebhookAt).getTime();
    const isHealthy = ageMs <= 5 * 60 * 1000;
    return {
      label: isHealthy ? "Webhook Live" : "Webhook Stale",
      isHealthy,
      title: `Last webhook: ${new Date(lastWebhookAt).toLocaleString()}`,
    };
  }, [webhookStatus]);

  const handleUpdateProfileImage = (newProfilePhoto) => {
    const formData = new FormData();
    formData.append("folderName", "profile");
    formData.append("files", newProfilePhoto);
    dispatch(setAuthLoading(true));
    dispatch(uploadFile(formData))
      .then((data) => {
        if (data.payload.data[0]) {
          dispatch(updateUserApi({ profileImage: data.payload.data[0] }));
        }
      })
      .catch((error) => {
        console.log(error);
        dispatch(setAuthLoading(false));
      });
  };

  const handleLogout = () => {
    dispatch(setProjectSliceToInitial());
    dispatch(clearToken());
    navigate("/login");
    setLogoutModalOpen(false);
  };

  return (
    <div className={`header h-16 flex justify-between items-center px-7`}>
      {!loading ? (
        <>
          <div className="flex items-center gap-3 header-left">
            <button
              onClick={handleToggle}
              className="header-nav"
              aria-label={
                isMobile
                  ? mobileMenuOpen
                    ? "Close navigation menu"
                    : "Open navigation menu"
                  : sidebarCollapsed
                    ? "Expand sidebar"
                    : "Collapse sidebar"
              }
              aria-expanded={isMobile ? mobileMenuOpen : undefined}
            >
              {isMobile ? (
                mobileMenuOpen ? (
                  <img src="/svg/nav-icon-close.svg" alt="" />
                ) : (
                  <img src="/svg/nav-icon.svg" alt="" />
                )
              ) : sidebarCollapsed ? (
                <img src="/svg/nav-icon-close.svg" alt="" />
              ) : (
                <img src="/svg/nav-icon.svg" alt="" />
              )}
            </button>
            {/* <div className="header-search">
              <img src="/svg/searchIcon.svg" alt="search" />
              <input placeholder="Search..." />
            </div> */}
          </div>
          {/* <div className="header-center">
            <HeaderTimeTrackerControls />
          </div> */}
          <div className="flex items-center justify-end gap-3 header-right">
            {isAdminRole && checkPermission(permissionType.ATTENDANCE, "read users") && (
              <div
                className={`webhook-indicator ${webhookHealth.isHealthy ? "healthy" : "stale"}`}
                title={webhookHealth.title}
              >
                <span className="dot" />
                <span>{webhookHealth.label}</span>
              </div>
            )}
            {/* <div className="header-nav cursor-pointer">
              <img src="/svg/notification-icon.svg" alt="notification" />
            </div> */}
            <div className="flex gap-2 items-center header-profile">
              <img
                onClick={() => navigate("/profile")}
                src={
                  user?.profileImage ??
                  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcToiRnzzyrDtkmRzlAvPPbh77E-Mvsk3brlxQ&s"
                }
                alt="profile-img"
                className="w-[34px] h-[34px] rounded-[50%] cursor-pointer"
              />
              <button
                type="button"
                className="text-left cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#744dff] rounded"
                title="Profile & password"
                onClick={() => navigate("/profile")}
              >
                <div className="text-xs header-role">{user?.roleName}</div>
                <div className="font-bold header-name">
                  {user?.firstname} {user?.lastname}
                </div>
              </button>
            </div>
            <button
              type="button"
              className="header-logout"
              onClick={() => setLogoutModalOpen(true)}
              aria-label="Log out"
              title="Log out"
            >
              <LogoutOutlined />
            </button>
          </div>
        </>
      ) : null}

      <ProfileImageModal
        isOpen={isProfileImageModalOpen}
        onClose={() => setIsProfileImageModalOpen(false)}
        currentImage={user?.profileImage}
        onUpdateImage={handleUpdateProfileImage}
      />

      <ConfirmModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
        title="Confirm Logout"
        content="Are you sure you want to logout?"
        confirmText="Logout"
      />
    </div>
  );
};

export default Header;
